import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useUser } from "@supabase/auth-helpers-react";
import { toast } from "sonner";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Building } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { useLoadScript } from "@react-google-maps/api";
import AddressAutocomplete from "@/components/AddressAutocomplete";

interface Country {
  code: string;
  name: string;
  extension: string;
}

const businessProfileSchema = z.object({
  business_name: z.string().min(1, "Business name is required"),
  business_email: z.string().email("Invalid email address"),
  business_phone: z.string().min(1, "Phone number is required"),
  extension: z.string(),
  street_address: z.string().min(1, "Street address is required"),
  address_line_2: z.string().nullish().transform(val => val || ""), // Handle null/undefined values
  city: z.string().min(1, "City is required"),
  region: z.string().nullish().transform(val => val || ""), // Handle null/undefined values
  postal_code: z.string().min(1, "Postal code is required"),
  country: z.string().min(1, "Country is required"),
  business_tax_id: z.string().min(1, "Tax ID is required"),
  business_iban: z.string().min(1, "IBAN is required"),
  business_swift: z.string().min(1, "SWIFT/BIC is required"),
  bank_name: z.string().min(1, "Bank name is required"),
});

type BusinessProfileFormData = z.infer<typeof businessProfileSchema>;

export function BusinessProfileForm() {
  const user = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const { t } = useTranslation();

  // Add Google Maps API loading state
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });

  const form = useForm<BusinessProfileFormData>({
    resolver: zodResolver(businessProfileSchema),
    defaultValues: {
      business_name: "",
      business_email: "",
      business_phone: "",
      extension: "",
      street_address: "",
      address_line_2: "",
      city: "",
      region: "",
      postal_code: "",
      country: "",
      business_tax_id: "",
      business_iban: "",
      business_swift: "",
      bank_name: "",
    },
  });

  // Add this effect to save form state to sessionStorage when it changes
  useEffect(() => {
    const formValues = form.getValues();
    if (Object.keys(formValues).some(key => formValues[key])) {
      sessionStorage.setItem('businessProfileFormState', JSON.stringify(formValues));
    }
  }, [form.watch()]);

  useEffect(() => {
    if (user) {
      fetchBusinessProfile();
      fetchCountries();
    }
  }, [user]);

  const fetchCountries = async () => {
    try {
      const { data, error } = await supabase
        .from('countries')
        .select('code, name, extension')
        .eq('is_enabled', true)
        .order('name');

      if (error) {
        return;
      }

      // Direct type assertion since we know the structure matches our interface
      setCountries(data as unknown as Country[]);

      // For debugging, you can log the data to verify its structure
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  };

  const fetchBusinessProfile = async () => {
    try {
      // Get current form values before fetching
      const currentFormValues = form.getValues();
      const hasFormData = Object.values(currentFormValues).some(val =>
        val !== undefined && val !== null && val !== "");

      // Always fetch from database first
      const { data, error } = await supabase
        .from("business_profiles")
        .select("*")
        .eq("user_id", user?.id)
        .maybeSingle();

      if (error) {
        toast.error(t("toasts.failed_load_business_profile"));

        // Only try sessionStorage as fallback if database fetch fails
        const savedState = sessionStorage.getItem('businessProfileFormState');
        if (savedState) {
          try {
            const parsedState = JSON.parse(savedState);
            Object.entries(parsedState).forEach(([key, value]) => {
              if (value) {
                form.setValue(key as any, value);
              }
            });
          } catch (error) {
            console.error("Error parsing saved form state:", error);
          }
        }
        return;
      }

      if (data) {
        // Set form values individually, but only if the database value is not empty
        // or if the current form value is empty
        Object.entries(data).forEach(([key, value]) => {
          // Only process fields that exist in our form
          if (key in currentFormValues) {
            const currentValue = currentFormValues[key as keyof typeof currentFormValues];
            // Only set the value if:
            // 1. The database value is not empty/null/undefined AND
            // 2. Either the current form value is empty OR we don't have any form data yet
            if (value && (currentValue === "" || currentValue === null || currentValue === undefined || !hasFormData)) {
              form.setValue(key as any, value);
            }
          }
        });

        setLogoUrl(data.logo_url);
        setProfileId(data.id);
      } else if (!hasFormData) {
        // No database data and no form data, try sessionStorage as fallback
        const savedState = sessionStorage.getItem('businessProfileFormState');
        if (savedState) {
          try {
            const parsedState = JSON.parse(savedState);
            Object.entries(parsedState).forEach(([key, value]) => {
              if (value) {
                form.setValue(key as any, value);
              }
            });
          } catch (error) {
            console.error("Error parsing saved form state:", error);
          }
        }
      }
    } catch (error) {
      toast.error(t("toasts.failed_load_business_profile"));
    }
  };

  // Add this effect to handle tab visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Force a re-render when tab becomes visible again
        const savedFormState = sessionStorage.getItem('businessProfileFormState');
        if (savedFormState) {
          try {
            const parsedState = JSON.parse(savedFormState);

            // Special handling for address fields
            // First set the address fields directly
            if (parsedState.street_address) {
              form.setValue('street_address', parsedState.street_address);
            }
            if (parsedState.address_line_2) {
              form.setValue('address_line_2', parsedState.address_line_2);
            }
            if (parsedState.city) {
              form.setValue('city', parsedState.city);
            }
            if (parsedState.region) {
              form.setValue('region', parsedState.region);
            }
            if (parsedState.postal_code) {
              form.setValue('postal_code', parsedState.postal_code);
            }
            if (parsedState.country) {
              form.setValue('country', parsedState.country);
            }

            // Then set all other fields
            Object.entries(parsedState).forEach(([key, value]) => {
              // Skip address fields as we've already handled them
              if (!['street_address', 'address_line_2', 'city', 'region', 'postal_code', 'country'].includes(key)) {
                if (value !== undefined && value !== null) {
                  form.setValue(key as any, value);
                }
              }
            });

            // Force form validation after setting values
            form.trigger();
          } catch (error) {
            console.error("Error parsing saved form state:", error);
          }
        }
      }
    };

    // Add event listener for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Clean up
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [form]);

  // Use a subscription to watch form changes and save to sessionStorage
  useEffect(() => {
    const subscription = form.watch((value) => {
      sessionStorage.setItem('businessProfileFormState', JSON.stringify(value));
    });

    return () => subscription.unsubscribe();
  }, [form]);

  const onSubmit = async (data: BusinessProfileFormData) => {
    try {
      setIsLoading(true);
      if (!user) return;

      const profileData = profileId
        ? {
          id: profileId,
          user_id: user.id,
          ...data,
          logo_url: logoUrl,
        }
        : {
          user_id: user.id,
          ...data,
          logo_url: logoUrl,
        };

      const { error } = await supabase
        .from("business_profiles")
        .upsert(profileData);

      if (error) {
        throw error;
      }

      if (!profileId) {
        await fetchBusinessProfile();
      }

      // Clear the saved form state after successful save
      sessionStorage.removeItem('businessProfileFormState');

      toast.success(t("toasts.business_profile_create_success"));
    } catch (error: any) {
      toast.error(t("toasts.business_profile_create_failed"), {
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setIsLoading(true);
      const file = event.target.files?.[0];
      if (!file || !user) return;

      if (!file.type.startsWith("image/")) {
        toast.error(t("toasts.load_image_file"));
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error(t("toasts.image_5mb"));
        return;
      }

      const fileExt = file.name.split(".").pop();
      const filePath = `${user.id}/${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("business_logos")
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      const { data: publicUrl } = supabase.storage
        .from("business_logos")
        .getPublicUrl(filePath);

      if (profileId) {
        const { error: updateError } = await supabase
          .from("business_profiles")
          .update({ logo_url: publicUrl.publicUrl })
          .eq('id', profileId);

        if (updateError) {
          throw updateError;
        }
      }

      setLogoUrl(publicUrl.publicUrl);
      toast.success(t("toasts.logo_upload_success"));
    } catch (error: any) {
      toast.error(t("toasts.logo_error_upload"), {
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogoDelete = async () => {
    try {
      setIsLoading(true);
      if (!logoUrl || !profileId) return;

      // Extract the file path from the public URL
      const urlParts = logoUrl.split('/');
      const filePath = urlParts.slice(urlParts.indexOf('business_logos') + 1).join('/');

      // Delete the file from storage
      const { error: deleteStorageError } = await supabase.storage
        .from("business_logos")
        .remove([filePath]);

      if (deleteStorageError) {
        throw deleteStorageError;
      }

      // Update the profile to remove the logo URL
      const { error: updateError } = await supabase
        .from("business_profiles")
        .update({ logo_url: null })
        .eq('id', profileId);

      if (updateError) {
        throw updateError;
      }

      // Update local state
      setLogoUrl(null);
      toast.success(t("toasts.logo_delete_success", "Logo deleted successfully"));
    } catch (error: any) {
      toast.error(t("toasts.logo_delete_failed", "Failed to delete logo"), {
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardContent className="space-y-6 bg-[#F1F6F5] rounded-lg">
        <div className="flex items-center space-x-4 pt-4">
          <div className="relative group">
            <Avatar className="w-20 h-20">
              <AvatarImage src={logoUrl || ''} alt="Business logo" className="object-contain bg-background" />
              <AvatarFallback className="text-muted-foreground bg-[#5CC2BB]">
                <Building className="h-10 w-10" />
              </AvatarFallback>
            </Avatar>
            {logoUrl && (
              <Button
                type="button"
                variant="destructive"
                size="icon"
                onClick={handleLogoDelete}
                disabled={isLoading}
                className="w-6 h-6 absolute bottom-0 right-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center p-0"
              >
                <span className="text-xs">×</span>
              </Button>
            )}
          </div>
          <div className="flex-1 pt-4">
            <div className="flex flex-col space-y-2">
              <Input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                disabled={isLoading}
                className="h-auto py-2 bg-white border border-gray-300 p-2 rounded-md"
              />
              <p className="text-sm font-thin text-muted-foreground mt-1">
                {t("account_settings.recommended_image")}
              </p>
            </div>
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 text-left">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="business_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#8791F3]">{t("seller_info.business_name", "Business Name")}</FormLabel>
                    <FormControl>
                      <Input className="bg-white border border-gray-300 p-2 rounded-md" placeholder={t("seller_info.business_name_placeholder", "Happy Co.")}{...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="business_tax_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#8791F3]">{t("seller_info.tax_id", "Tax ID")}</FormLabel>
                    <FormControl>
                      <Input className="bg-white border border-gray-300 p-2 rounded-md" placeholder={t("seller_info.tax_id_number", "Tax ID number")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="business_email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#8791F3]">{t("seller_info.email", "Email")}</FormLabel>
                    <FormControl>
                      <Input className="bg-white border border-gray-300 p-2 rounded-md" placeholder={t("seller_info.email_placeholder", "contact@gmail.com")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="extension"
                  render={({ field }) => (
                    <FormItem className="col-span-1">
                      <FormLabel>{t("seller_info.extension", "Extension")}*</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="text-[#34375B] font-normal bg-white border border-gray-300 p-2 rounded-md">
                            <SelectValue placeholder={t("seller_info.extension_placeholder", "Select extension")} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-white border border-gray-300 p-2 rounded-md">
                          {countries.map((country) => (
                            <SelectItem key={country.extension} value={country.extension}>
                              {country.extension}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="business_phone"
                  render={({ field }) => (
                    <FormItem className="col-span-2">
                      <FormLabel className="text-[#8791F3]">{t("seller_info.phone", "Phone")}</FormLabel>
                      <FormControl>
                        <Input className="bg-white border border-gray-300 p-2 rounded-md" placeholder="+1234567890" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="street_address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#8791F3]">{t("account_settings.address_line_1", "Street Name")}</FormLabel>
                    <FormControl>
                      {isLoaded ? (
                        <AddressAutocomplete
                          value={field.value}
                          onChange={(address, details) => {
                            field.onChange(address);

                            // Auto-fill other address fields if details are available
                            if (details) {
                              // Set postal code
                              if (details.postalCode) {
                                form.setValue('postal_code', details.postalCode);
                              }

                              // Set city
                              if (details.city) {
                                form.setValue('city', details.city);
                              }

                              // Set region/state
                              if (details.region) {
                                form.setValue('region', details.region);
                              }

                              // Set country - find the matching country code in countries array
                              if (details.country) {
                                const countryMatch = countries.find(c => c.code === details.country);
                                if (countryMatch) {
                                  form.setValue('country', countryMatch.code);
                                }
                              }
                            }
                          }}
                          className="bg-white border border-gray-300 p-2 rounded-md"
                          placeholder={t("buyer_info.client_address_placeholder", "221B Baker Street")}
                        />
                      ) : (
                        <Input
                          className="bg-white border border-gray-300 p-2 rounded-md"
                          placeholder={t("buyer_info.client_address_placeholder", "221B Baker Street")}
                          {...field}
                        />
                      )}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address_line_2"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#8791F3]">{t("account_settings.address_line_2", "Address Line 2")}</FormLabel>
                    <FormControl>
                      <Input className="bg-white border border-gray-300 p-2 rounded-md" placeholder={t("account_settings.address_line_2_placeholder", "Suite 100 (Optional)")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#8791F3]">{t("account_settings.city", "City")}</FormLabel>
                    <FormControl>
                      <Input className="bg-white border border-gray-300 p-2 rounded-md" placeholder={t("account_settings.city", "City")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="region"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#8791F3]">{t("account_settings.region", "Region/State")}</FormLabel>
                    <FormControl>
                      <Input className="bg-white border border-gray-300 p-2 rounded-md" placeholder={t("account_settings.region_placeholder", "Region (Optional)")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="postal_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#8791F3]">{t("account_settings.postal_code", "Postal Code")}</FormLabel>
                    <FormControl>
                      <Input className="bg-white border border-gray-300 p-2 rounded-md" placeholder={t("account_settings.postal_code_placeholder", "1234-567")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#8791F3]">{t("account_settings.country", "Country")}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="text-[#34375B] font-normal bg-white border border-gray-300 p-2 rounded-md">
                          <SelectValue placeholder={t("account_settings.country_select", "Select a country")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-white border border-gray-300 p-2 rounded-md">
                        {countries.map((country) => (
                          <SelectItem key={country.code} value={country.code}>
                            {country.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>


            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="business_iban"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#8791F3]">{t("seller_info.iban", "IBAN")}</FormLabel>
                    <FormControl>
                      <Input className="bg-white border border-gray-300 p-2 rounded-md" placeholder={t("seller_info.iban", "IBAN number")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="business_swift"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#8791F3]">{t("seller_info.swift_bic", "SWIFT/BIC")}</FormLabel>
                    <FormControl>
                      <Input className="bg-white border border-gray-300 p-2 rounded-md" placeholder={t("seller_info.swift_bic_code", "SWIFT/BIC code")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="bank_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#8791F3]">{t("seller_info.bank_name", "Bank Name")}</FormLabel>
                    <FormControl>
                      <Input className="bg-white border border-gray-300 p-2 rounded-md" placeholder={t("seller_info.bank_name_placeholder", "Bank name")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end">
              <Button className="bg-[#8791F3] hover:bg-[#5CC2BB] hover:text-[#34375B] hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-1 hover:-translate-y-1 transition-all duration-200" type="submit" disabled={isLoading}>
                {isLoading ? t("common.saving", "Saving...") : t("common.save_changes", "Save Changes")}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
